import type { HailReportTypeRules } from '@xweather/protect-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { z } from 'zod'
import { useHailDataAvailability } from '../../../../data/analyze/reportTypeRules.ts'
import type { AssetLocation } from '../../../../data/locations.tsx'
import { dayjs } from '../../../../dayjs.ts'
import { useDistanceRangeHelpers } from '../../../distance-range-helpers.ts'
import { createUseFormValues } from '../../createUseFormValues.ts'
import {
	coverageZoneOf,
	exceedsMaxArea,
	exceedsMaxDuration,
	isShorterThanMinDuration,
	maxAreaKm2,
	maxDurationForArea,
} from '../reportTypeRules.ts'
import { BaseReportFormSchema, ReportTimeRangeSchema, useBaseReportDefaultValues } from '../schema.ts'

export const HailReportFormSchema = BaseReportFormSchema.extend({
	timeRange: ReportTimeRangeSchema,
})

export type HailReportForm = z.infer<typeof HailReportFormSchema>
export const useHailFormValues = createUseFormValues<HailReportForm>()

// Whole minutes, as the date fields take them; the backend accepts a start up to a few minutes early
export const inWholeMinutes = ({ start, end }: { start: string; end: string }) => ({
	start: dayjs(start).startOf('minute'),
	end: dayjs(end).startOf('minute'),
})

export const useHailFormSchema = (rules: HailReportTypeRules | undefined) => {
	const { data: hailDataAvailability } = useHailDataAvailability()
	const { fromKm2ToUserPreference, formattedUnit } = useDistanceRangeHelpers()
	const { t } = useTranslation()

	return useMemo(() => {
		// Hail rules are missing only while analyzeHailReports is off, so remove this check when the flag is removed
		if (!rules) return HailReportFormSchema

		return HailReportFormSchema.superRefine(({ geometry, timeRange }, ctx) => {
			if (exceedsMaxArea(rules, geometry)) {
				ctx.addIssue({
					code: 'custom',
					message: t(($) => $.reports.form.areaMaxExceeded, {
						maxArea: Math.floor(fromKm2ToUserPreference(maxAreaKm2(rules))),
						unit: formattedUnit,
					}),
					path: ['geometry'],
				})
			}

			const zone = coverageZoneOf(rules.coverageZones, geometry)
			if (!zone) {
				ctx.addIssue({ code: 'custom', message: t(($) => $.reports.hail.form.outsideHailCoverage), path: ['geometry'] })
				return
			}

			const dataAvailability = hailDataAvailability && inWholeMinutes(hailDataAvailability.coverageZones[zone])
			if (dataAvailability) {
				const isOutsideDataAvailability = (date: string) =>
					dayjs(date).isBefore(dataAvailability.start) || dayjs(date).isAfter(dataAvailability.end)
				for (const edge of ['start', 'end'] as const) {
					if (isOutsideDataAvailability(timeRange[edge])) {
						ctx.addIssue({ code: 'custom', message: t(($) => $.reports.form.dateOutsideDataAvailability), path: ['timeRange', edge] })
					}
				}
			}

			const { start, end } = timeRange
			if (!start || !end || !dayjs(end).isAfter(start)) return

			const addTimeRangeIssue = (message: string) => ctx.addIssue({ code: 'custom', message, path: ['timeRange'] })
			const duration = dayjs.duration(dayjs(end).diff(start))
			const maxDuration = maxDurationForArea(rules, geometry)

			if (isShorterThanMinDuration(rules, duration)) {
				addTimeRangeIssue(t(($) => $.reports.hail.form.durationMinNotReached, { count: dayjs.duration(rules.minDuration).asHours() }))
			}

			if (maxDuration && exceedsMaxDuration(rules, maxDuration, duration)) {
				addTimeRangeIssue(t(($) => $.reports.hail.form.durationMaxExceeded, { count: maxDuration.asHours() }))
			}
		})
	}, [rules, hailDataAvailability, fromKm2ToUserPreference, formattedUnit, t])
}

export const useHailDefaultValues = (selectedLocations: AssetLocation[]): HailReportForm => ({
	...useBaseReportDefaultValues(selectedLocations),
	timeRange: { start: '', end: '' },
})
